import React, { useState, useEffect, useCallback } from 'react';
import { DollarSign, ChevronDown, ChevronRight } from 'lucide-react';
import { adminCommissions, adminCustomers } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';

// ── Helpers ───────────────────────────────────────────────────────────────────

const firstOfMonth = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};

const todayStr = () => new Date().toISOString().slice(0, 10);

const fmt$ = (v) => {
  if (v == null) return '$0.00';
  return Number(v).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

const fmtDate = (d) => {
  if (!d) return '—';
  const p = new Date(d);
  if (isNaN(p.getTime())) return '—';
  return p.toLocaleDateString('en-US');
};

const BONUS_LABELS = {
  new_account_3case: 'New Account – 3+ Cases',
  new_account_1case: 'New Account – 1–2 Cases',
};

// ── CommissionReport ──────────────────────────────────────────────────────────
// Tab panel rendered inside ReportsDashboard.
// Fetches commissions, incentive payouts, summary, and disbursements
// based on the applied filter set.

export default function CommissionReport() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  // ── Filter bar state (controlled but NOT applied until button clicked) ─────
  const [startDate, setStartDate] = useState(firstOfMonth);
  const [endDate,   setEndDate]   = useState(todayStr);
  const [repID,     setRepID]     = useState('');
  const [status,    setStatus]    = useState('all');

  // Applied snapshot drives fetches
  const [applied, setApplied] = useState({
    startDate: firstOfMonth(),
    endDate:   todayStr(),
    repID:     '',
    status:    'all',
  });

  // ── Data ──────────────────────────────────────────────────────────────────
  const [reps,          setReps]          = useState([]);
  const [summary,       setSummary]       = useState([]);
  const [commissions,   setCommissions]   = useState([]);
  const [incentives,    setIncentives]    = useState([]);
  const [disbursements, setDisbursements] = useState([]);
  const [loading,       setLoading]       = useState(false);

  // ── Selection (admin only) ────────────────────────────────────────────────
  const [selectedComm, setSelectedComm] = useState(new Set());
  const [selectedInc,  setSelectedInc]  = useState(new Set());

  // ── Disbursement modal ────────────────────────────────────────────────────
  const [showDisburse,  setShowDisburse]  = useState(false);
  const [disburseForm,  setDisburseForm]  = useState({
    periodStart: '', periodEnd: '', paidDate: todayStr(), note: '',
  });
  const [disburseError, setDisburseError] = useState('');
  const [disbursing,    setDisbursing]    = useState(false);

  // ── Disbursement history ──────────────────────────────────────────────────
  const [showHistory, setShowHistory] = useState(false);

  // ── Toast ─────────────────────────────────────────────────────────────────
  const [toast, setToast] = useState(null);
  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  // ── Load rep list (admin only) ─────────────────────────────────────────────
  useEffect(() => {
    if (!isAdmin) return;
    adminCustomers.reps()
      .then(r => setReps(r.data.reps || []))
      .catch(() => {});
  }, [isAdmin]);

  // ── Fetch data when applied snapshot changes ───────────────────────────────
  const doFetch = useCallback(async (f) => {
    setLoading(true);
    // Map unified status → backend-specific params
    const commStatus = f.status === 'paid' ? 'disbursed' : f.status === 'earned' ? 'undisbursed' : 'all';
    const incStatus  = f.status === 'paid' ? 'paid'      : f.status === 'earned' ? 'earned'      : 'all';
    const repFilter  = f.repID ? { repID: f.repID } : {};

    try {
      const [commRes, incRes, sumRes, disbRes] = await Promise.all([
        adminCommissions.list({
          dateFrom: f.startDate, dateTo: f.endDate, status: commStatus, ...repFilter,
        }),
        adminCommissions.incentivePayouts({
          startDate: f.startDate, endDate: f.endDate, status: incStatus, ...repFilter,
        }),
        adminCommissions.summary({
          dateFrom: f.startDate, dateTo: f.endDate, ...repFilter,
        }),
        adminCommissions.disbursements({ ...repFilter }),
      ]);
      setCommissions(commRes.data.data   || []);
      setIncentives(incRes.data.data     || []);
      setSummary(sumRes.data.data        || []);
      setDisbursements(disbRes.data.data || []);
      setSelectedComm(new Set());
      setSelectedInc(new Set());
    } catch {
      showToast('Failed to load commission data.', 'error');
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    doFetch(applied);
  }, [applied, doFetch]);

  const handleApply = () => setApplied({ startDate, endDate, repID, status });

  // ── Summary aggregates ─────────────────────────────────────────────────────
  const totalEarned  = summary.reduce((s, r) => s + parseFloat(r.totalEarned      || 0), 0);
  const totalPaid    = summary.reduce((s, r) => s + parseFloat(r.disbursedAmount  || 0), 0);
  const totalPending = summary.reduce((s, r) => s + parseFloat(r.pendingAmount    || 0), 0);
  const incPending   = summary.reduce((s, r) => s + parseFloat(r.pendingIncentive || 0), 0);

  // ── Commission selection ───────────────────────────────────────────────────
  const undisbursedComm = commissions.filter(c => !c.disbursementID);
  const toggleComm = (id) => setSelectedComm(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const toggleAllComm = () => {
    setSelectedComm(
      selectedComm.size === undisbursedComm.length
        ? new Set()
        : new Set(undisbursedComm.map(c => c.commissionID))
    );
  };

  // ── Incentive selection ────────────────────────────────────────────────────
  const earnedInc = incentives.filter(i => i.status === 'earned');
  const toggleInc = (id) => setSelectedInc(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const toggleAllInc = () => {
    setSelectedInc(
      selectedInc.size === earnedInc.length
        ? new Set()
        : new Set(earnedInc.map(i => i.payoutID))
    );
  };

  const anySelected = selectedComm.size > 0 || selectedInc.size > 0;

  // ── Disburse helpers ───────────────────────────────────────────────────────
  const getSelectedRepInfo = () => {
    const repIDs = new Set();
    const repNames = new Set();
    for (const id of selectedComm) {
      const c = commissions.find(x => x.commissionID === id);
      if (c) { repIDs.add(c.repID); repNames.add(c.repName); }
    }
    for (const id of selectedInc) {
      const inc = incentives.find(x => x.payoutID === id);
      if (inc) { repIDs.add(inc.repID); repNames.add(inc.repName); }
    }
    return { repIDs, repName: [...repNames][0] || '' };
  };

  const getSelectedTotal = () => {
    let total = 0;
    for (const id of selectedComm) {
      const c = commissions.find(x => x.commissionID === id);
      if (c) total += parseFloat(c.commissionAmount || 0);
    }
    for (const id of selectedInc) {
      const inc = incentives.find(x => x.payoutID === id);
      if (inc) total += parseFloat(inc.amount || 0);
    }
    return total;
  };

  const openDisburse = () => {
    const { repIDs } = getSelectedRepInfo();
    if (repIDs.size > 1) {
      showToast('All selected items must belong to the same rep.', 'error');
      return;
    }
    setDisburseForm({
      periodStart: applied.startDate,
      periodEnd:   applied.endDate,
      paidDate:    todayStr(),
      note:        '',
    });
    setDisburseError('');
    setShowDisburse(true);
  };

  const handleDisburse = async () => {
    if (!disburseForm.periodStart || !disburseForm.periodEnd || !disburseForm.paidDate) {
      setDisburseError('Period dates and paid date are required.');
      return;
    }
    const { repIDs } = getSelectedRepInfo();
    const repIDVal = [...repIDs][0];
    if (!repIDVal) return;

    setDisbursing(true);
    setDisburseError('');
    try {
      await adminCommissions.disburse({
        repID:              repIDVal,
        periodStart:        disburseForm.periodStart,
        periodEnd:          disburseForm.periodEnd,
        paidDate:           disburseForm.paidDate,
        note:               disburseForm.note || undefined,
        commissionIDs:      [...selectedComm],
        incentivePayoutIDs: [...selectedInc],
      });
      showToast('Disbursement recorded successfully.');
      setShowDisburse(false);
      doFetch(applied);
    } catch (e) {
      setDisburseError(e.response?.data?.message || 'Failed to record disbursement.');
    } finally {
      setDisbursing(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      {/* Toast */}
      {toast && (
        <div
          className={`toast show border-0 shadow text-white bg-${toast.type === 'error' ? 'danger' : 'success'}`}
          style={{ position: 'fixed', top: 16, right: 16, zIndex: 9999, minWidth: 280 }}
        >
          <div className="d-flex align-items-center px-3 py-2">
            <span className="me-auto">{toast.message}</span>
            <button className="btn-close btn-close-white ms-2" onClick={() => setToast(null)} />
          </div>
        </div>
      )}

      {/* ── 1. Filter bar ─────────────────────────────────────────────────── */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body">
          <div className="row g-3 align-items-end">
            <div className="col-auto">
              <label className="form-label small text-muted mb-1">Start Date</label>
              <input
                type="date" className="form-control form-control-sm"
                value={startDate} onChange={e => setStartDate(e.target.value)}
              />
            </div>
            <div className="col-auto">
              <label className="form-label small text-muted mb-1">End Date</label>
              <input
                type="date" className="form-control form-control-sm"
                value={endDate} onChange={e => setEndDate(e.target.value)}
              />
            </div>
            {isAdmin && (
              <div className="col-auto">
                <label className="form-label small text-muted mb-1">Rep</label>
                <select className="form-select form-select-sm" value={repID}
                  onChange={e => setRepID(e.target.value)}>
                  <option value="">All Reps</option>
                  {reps.map(r => (
                    <option key={r.id} value={r.id}>{r.firstName} {r.lastName}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="col-auto">
              <label className="form-label small text-muted mb-1">Status</label>
              <select className="form-select form-select-sm" value={status}
                onChange={e => setStatus(e.target.value)}>
                <option value="all">All</option>
                <option value="earned">Earned (unpaid)</option>
                <option value="paid">Paid</option>
              </select>
            </div>
            <div className="col-auto">
              <button className="btn btn-primary btn-sm" onClick={handleApply} disabled={loading}>
                {loading
                  ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                  : 'Apply Filters'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── 2. Summary cards ──────────────────────────────────────────────── */}
      <div className="row g-3 mb-4">
        <div className="col-md-3">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <p className="text-muted small mb-1">Total Commissions Earned</p>
              <h4 className="fw-bold mb-0">{fmt$(totalEarned)}</h4>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <p className="text-muted small mb-1">Total Commissions Paid</p>
              <h4 className="fw-bold mb-0">{fmt$(totalPaid)}</h4>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <p className="text-muted small mb-1">Total Pending</p>
              <h4 className="fw-bold mb-0 text-warning">{fmt$(totalPending)}</h4>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <p className="text-muted small mb-1">Incentive Bonuses Pending</p>
              <h4 className="fw-bold mb-0 text-info">{fmt$(incPending)}</h4>
            </div>
          </div>
        </div>
      </div>

      {/* Disburse Selected button */}
      {isAdmin && anySelected && (
        <div className="mb-3">
          <button
            className="btn btn-success d-inline-flex align-items-center gap-2"
            onClick={openDisburse}
          >
            <DollarSign size={16} />
            Disburse Selected ({selectedComm.size + selectedInc.size} items — {fmt$(getSelectedTotal())})
          </button>
        </div>
      )}

      {/* ── 3. Commissions table ──────────────────────────────────────────── */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-header bg-light fw-semibold">Commissions</div>
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status" />
            </div>
          ) : commissions.length === 0 ? (
            <p className="text-muted text-center py-4 mb-0">
              No commission records for this period.
            </p>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    {isAdmin && (
                      <th style={{ width: 36 }}>
                        <input
                          type="checkbox" className="form-check-input"
                          checked={undisbursedComm.length > 0 && selectedComm.size === undisbursedComm.length}
                          onChange={toggleAllComm}
                        />
                      </th>
                    )}
                    <th className="text-muted small fw-semibold">Order #</th>
                    <th className="text-muted small fw-semibold">Date</th>
                    <th className="text-muted small fw-semibold">Rep</th>
                    <th className="text-muted small fw-semibold text-end">Order Total</th>
                    <th className="text-muted small fw-semibold text-end">Rate</th>
                    <th className="text-muted small fw-semibold text-end">Commission</th>
                    <th className="text-muted small fw-semibold">Earned Date</th>
                    <th className="text-muted small fw-semibold">Status</th>
                    <th className="text-muted small fw-semibold">Disbursement Date</th>
                  </tr>
                </thead>
                <tbody>
                  {commissions.map(c => (
                    <tr key={c.commissionID}>
                      {isAdmin && (
                        <td>
                          {!c.disbursementID && (
                            <input
                              type="checkbox" className="form-check-input"
                              checked={selectedComm.has(c.commissionID)}
                              onChange={() => toggleComm(c.commissionID)}
                            />
                          )}
                        </td>
                      )}
                      <td className="fw-medium small">#{c.orderID}</td>
                      <td className="text-muted small">{fmtDate(c.orderDate)}</td>
                      <td className="small">{c.repName}</td>
                      <td className="text-end small">{fmt$(c.orderTotal)}</td>
                      <td className="text-end small">{c.rateSnapshot}%</td>
                      <td className="text-end fw-semibold">{fmt$(c.commissionAmount)}</td>
                      <td className="small">{fmtDate(c.earnedDate)}</td>
                      <td>
                        {c.disbursementID
                          ? <span className="badge bg-success">Paid</span>
                          : <span className="badge bg-warning text-dark">Pending</span>}
                      </td>
                      <td className="small text-muted">
                        {c.disbursementDate ? fmtDate(c.disbursementDate) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ── 4. Incentive Bonuses table ────────────────────────────────────── */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-header bg-light fw-semibold">Incentive Bonuses</div>
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status" />
            </div>
          ) : incentives.length === 0 ? (
            <p className="text-muted text-center py-4 mb-0">
              No incentive bonus records for this period.
            </p>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    {isAdmin && (
                      <th style={{ width: 36 }}>
                        <input
                          type="checkbox" className="form-check-input"
                          checked={earnedInc.length > 0 && selectedInc.size === earnedInc.length}
                          onChange={toggleAllInc}
                        />
                      </th>
                    )}
                    <th className="text-muted small fw-semibold">Account</th>
                    <th className="text-muted small fw-semibold">Order #</th>
                    <th className="text-muted small fw-semibold">Order Date</th>
                    <th className="text-muted small fw-semibold">Rep</th>
                    <th className="text-muted small fw-semibold">Bonus Type</th>
                    <th className="text-muted small fw-semibold text-end">Amount</th>
                    <th className="text-muted small fw-semibold">Earned Date</th>
                    <th className="text-muted small fw-semibold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {incentives.map(inc => (
                    <tr key={inc.payoutID}>
                      {isAdmin && (
                        <td>
                          {inc.status === 'earned' && (
                            <input
                              type="checkbox" className="form-check-input"
                              checked={selectedInc.has(inc.payoutID)}
                              onChange={() => toggleInc(inc.payoutID)}
                            />
                          )}
                        </td>
                      )}
                      <td className="fw-medium small">{inc.companyName}</td>
                      <td className="small">#{inc.orderID}</td>
                      <td className="text-muted small">{fmtDate(inc.orderDate)}</td>
                      <td className="small">{inc.repName}</td>
                      <td className="small">{BONUS_LABELS[inc.bonusType] || inc.bonusType}</td>
                      <td className="text-end fw-semibold">{fmt$(inc.amount)}</td>
                      <td className="small">{fmtDate(inc.earnedDate)}</td>
                      <td>
                        {inc.status === 'paid'
                          ? <span className="badge bg-success">Paid</span>
                          : <span className="badge bg-info text-dark">Earned</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ── 5. Disbursement History (collapsible) ────────────────────────── */}
      <div className="card border-0 shadow-sm mb-4">
        <div
          className="card-header bg-light d-flex align-items-center justify-content-between"
          style={{ cursor: 'pointer' }}
          onClick={() => setShowHistory(h => !h)}
          role="button"
          tabIndex={0}
          onKeyDown={e => e.key === 'Enter' && setShowHistory(h => !h)}
        >
          <span className="fw-semibold">Disbursement History</span>
          {showHistory ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </div>
        {showHistory && (
          <div className="card-body p-0">
            {disbursements.length === 0 ? (
              <p className="text-muted text-center py-4 mb-0">No disbursements recorded.</p>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th className="text-muted small fw-semibold">Rep</th>
                      <th className="text-muted small fw-semibold">Period</th>
                      <th className="text-muted small fw-semibold">Paid Date</th>
                      <th className="text-muted small fw-semibold text-end">Amount</th>
                      <th className="text-muted small fw-semibold">Note</th>
                      <th className="text-muted small fw-semibold text-end">Items</th>
                    </tr>
                  </thead>
                  <tbody>
                    {disbursements.map(d => (
                      <tr key={d.disbursementID}>
                        <td className="fw-medium small">{d.repName}</td>
                        <td className="small text-muted">
                          {fmtDate(d.periodStart)} – {fmtDate(d.periodEnd)}
                        </td>
                        <td className="small">{fmtDate(d.paidDate)}</td>
                        <td className="text-end fw-semibold">{fmt$(d.totalAmount)}</td>
                        <td className="small text-muted">{d.note || '—'}</td>
                        <td className="text-end small text-muted">
                          {Number(d.commissionCount) + Number(d.incentiveCount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Disburse modal ────────────────────────────────────────────────── */}
      {showDisburse && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,.45)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Record Disbursement</h5>
                <button className="btn-close" onClick={() => setShowDisburse(false)} />
              </div>
              <div className="modal-body">
                <div className="mb-3 p-3 bg-light rounded">
                  <p className="mb-1 small text-muted">Rep</p>
                  <p className="fw-semibold mb-0">{getSelectedRepInfo().repName}</p>
                </div>
                <div className="mb-3 p-3 bg-success bg-opacity-10 rounded">
                  <p className="mb-1 small text-muted">Total to Disburse</p>
                  <h4 className="fw-bold mb-1 text-success">{fmt$(getSelectedTotal())}</h4>
                  <p className="small text-muted mb-0">
                    {selectedComm.size} commission{selectedComm.size !== 1 ? 's' : ''}
                    {selectedInc.size > 0 && (
                      <> + {selectedInc.size} incentive bonus{selectedInc.size !== 1 ? 'es' : ''}</>
                    )}
                  </p>
                </div>

                {disburseError && (
                  <div className="alert alert-danger py-2 small mb-3">{disburseError}</div>
                )}

                <div className="row g-2 mb-3">
                  <div className="col">
                    <label className="form-label">Period Start</label>
                    <input
                      type="date" className="form-control"
                      value={disburseForm.periodStart}
                      onChange={e => setDisburseForm(f => ({ ...f, periodStart: e.target.value }))}
                    />
                  </div>
                  <div className="col">
                    <label className="form-label">Period End</label>
                    <input
                      type="date" className="form-control"
                      value={disburseForm.periodEnd}
                      onChange={e => setDisburseForm(f => ({ ...f, periodEnd: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label">Paid Date</label>
                  <input
                    type="date" className="form-control"
                    value={disburseForm.paidDate}
                    onChange={e => setDisburseForm(f => ({ ...f, paidDate: e.target.value }))}
                  />
                </div>
                <div className="mb-2">
                  <label className="form-label">
                    Note <span className="text-muted small">(optional)</span>
                  </label>
                  <input
                    className="form-control"
                    placeholder='e.g. "Q1 2026 payroll"'
                    value={disburseForm.note}
                    onChange={e => setDisburseForm(f => ({ ...f, note: e.target.value }))}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowDisburse(false)}>
                  Cancel
                </button>
                <button
                  className="btn btn-success d-inline-flex align-items-center gap-1"
                  onClick={handleDisburse}
                  disabled={disbursing}
                >
                  {disbursing
                    ? <span className="spinner-border spinner-border-sm" />
                    : <DollarSign size={14} />}
                  Confirm &amp; Record Disbursement
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
